import React, { ReactElement, useRef } from 'react';

import { widgetMessages } from '../../../../../../../translates/widgetMessages';
import { TranslateResourceStringFnType, useIntlReexport } from '../../../../../../hooks/useIntlReexport';
import { AnswerMessage } from '../../../../../components/common/Widget/AnswerMessage';
import { SubmitStatus, SubmittedData, SubmittedDataFail } from '../QuestionComponent';
import { AnswerFeedback } from '@ael/core-api';
import { partition } from 'lodash';
import { CSSTransition } from 'react-transition-group';

import './QuestionFooter.scss';

type QuestionFooterProps = {
  button: ReactElement;
  submittedData: SubmittedData;
  customFeedback: AnswerFeedback;
  repeatUntilCorrect: boolean;
};

const generateFailedMessage = (
  submittedData: SubmittedDataFail,
  formatMessage: TranslateResourceStringFnType,
  repeatUntilCorrect: boolean
) => {
  const selectedAndCorrect = submittedData.correct.filter((answer) => answer.isCorrect).length;
  const [notSelectedButCorrect, selectedButIncorrect] = partition(
    submittedData.incorrect,
    (answer) => answer.isCorrect
  );

  // if totally wrong
  if (selectedAndCorrect === 0) {
    return repeatUntilCorrect
      ? formatMessage(widgetMessages.questionWidgetIncorrectTryMessage)
      : formatMessage(widgetMessages.questionWidgetIncorrectNoRetry);
  } else {
    // if partially wrong
    if (notSelectedButCorrect.length > 0) {
      if (selectedButIncorrect.length > 0) {
        return formatMessage(widgetMessages.questionWidgetPartiallyIncorrectMissingAndWrongOptions, {
          countMissing: notSelectedButCorrect.length,
          countIncorrect: selectedButIncorrect.length,
        });
      }
      return formatMessage(widgetMessages.questionWidgetPartiallyIncorrectMissingOptions, {
        count: notSelectedButCorrect.length,
      });
    }
    return formatMessage(widgetMessages.questionWidgetPartiallyIncorrectMarkedWrong, {
      count: selectedButIncorrect.length,
    });
  }
};

export const QuestionFooter: React.FC<QuestionFooterProps> = ({
  button,
  submittedData,
  customFeedback,
  repeatUntilCorrect,
}) => {
  const { formatMessage } = useIntlReexport();
  const ref = useRef(null);

  return (
    <CSSTransition nodeRef={ref} timeout={500} classNames="fade" in appear>
      <div ref={ref} className="question-footer">
        {(submittedData.status === SubmitStatus.NotSubmitted ||
          (submittedData.status === SubmitStatus.SubmittedAndFailed && repeatUntilCorrect)) && <div>{button}</div>}

        {submittedData.status === SubmitStatus.SubmittedAndSucceded && (
          <AnswerMessage variant="success">
            {customFeedback.feedbackCorrect
              ? customFeedback.feedbackCorrect
              : formatMessage(widgetMessages.questionWidgetCorrectTryMessage)}
          </AnswerMessage>
        )}
        {submittedData.status === SubmitStatus.SubmittedAndFailed && (
          <AnswerMessage variant="fail">
            {customFeedback.feedbackIncorrect
              ? customFeedback.feedbackIncorrect
              : generateFailedMessage(submittedData, formatMessage, repeatUntilCorrect)}
          </AnswerMessage>
        )}
      </div>
    </CSSTransition>
  );
};
